/**
 * THROWAWAY DEMO CODE (Pillar procurement demo). Static data for the BoM
 * forecast: the three panel-shop finished goods, their bills of materials and
 * the component supply position (on hand, open POs, safety stock, lead time,
 * pack rules). Numbers follow the README "Forecasting" tables. All time-phased
 * arrays hold six monthly buckets, M1 to M6.
 */
import { FG_PLAN } from './forecast-data';
import type {
  ContractId,
  MaterialId,
  SiteId,
  SupplierId,
} from '../../data/types';

export const FG_SKUS = ['PPC-MCC-480', 'PPC-PDP-208', 'KIT-VFD-50'] as const;
export type FgSku = (typeof FG_SKUS)[number];

export const COMPONENT_SKUS = [
  'ENC-N12-7224',
  'ENC-N4X-2420',
  'MCCB-800A',
  'VFD-50HP-480',
  'MCB-20A-1P',
  'THHN-1/0-CU',
] as const;
export type ComponentSku = (typeof COMPONENT_SKUS)[number];

export const BUCKET_COUNT = 6;

/**
 * Days from today to the first build day of each monthly bucket. The current
 * week is frozen, so M1 starts in 9 days; later buckets follow at calendar
 * month spacing. Fixed offsets keep the demo identical on any day it is run.
 */
export const BUCKET_START_DAYS: readonly number[] = [9, 39, 70, 100, 131, 161];

/** Planned orders releasing within this many days (or already late) are ready to send. */
export const RELEASE_WINDOW_DAYS = 28;

/** Buckets that fall inside the "next 8 weeks" risk window (M1 and M2). */
export const RISK_WINDOW_BUCKETS = 2;

/** The Hollis alternate is approved for 6 weeks, while SCAR-0192 is open. */
export const ALTERNATE_EFFECTIVITY_DAYS = 42;

export const PANEL_SHOP_SITE: SiteId = 'PNL';

export interface FinishedGood {
  sku: FgSku;
  name: string;
  description: string;
  /**
   * The model forecast, units per bucket: the first six months of the forecast
   * plan. The parts plan itself runs on the forecast store (`bom-plan.ts`).
   */
  forecast: number[];
  /** Series id used by the forecast details page, `${sku}@${siteId}`. */
  seriesId: string;
}

export const FINISHED_GOODS: FinishedGood[] = [
  {
    sku: 'PPC-MCC-480',
    name: 'Motor control panel, 480 V, 6 sections',
    description:
      'UL-certified motor control center built to order in the panel shop',
    forecast: FG_PLAN['PPC-MCC-480'].slice(0, BUCKET_COUNT),
    seriesId: 'PPC-MCC-480@PNL',
  },
  {
    sku: 'PPC-PDP-208',
    name: 'Power distribution panel, 208 V',
    description:
      'UL-certified distribution panel with a 24-circuit branch section',
    forecast: FG_PLAN['PPC-PDP-208'].slice(0, BUCKET_COUNT),
    seriesId: 'PPC-PDP-208@PNL',
  },
  {
    sku: 'KIT-VFD-50',
    name: 'Motor drive retrofit kit, 50 HP',
    description:
      'Drive, enclosure and wire kit for retrofitting existing motor starters',
    forecast: FG_PLAN['KIT-VFD-50'].slice(0, BUCKET_COUNT),
    seriesId: 'KIT-VFD-50@PNL',
  },
];

export interface BomLine {
  fg: FgSku;
  component: ComponentSku;
  /** Quantity of the component per finished good, in the component's UOM. */
  qtyPer: number;
}

export const BOM_LINES: BomLine[] = [
  { fg: 'PPC-MCC-480', component: 'ENC-N12-7224', qtyPer: 1 },
  { fg: 'PPC-MCC-480', component: 'MCCB-800A', qtyPer: 1 },
  { fg: 'PPC-MCC-480', component: 'VFD-50HP-480', qtyPer: 6 },
  { fg: 'PPC-MCC-480', component: 'THHN-1/0-CU', qtyPer: 4 },
  { fg: 'PPC-PDP-208', component: 'ENC-N12-7224', qtyPer: 1 },
  { fg: 'PPC-PDP-208', component: 'MCCB-800A', qtyPer: 1 },
  { fg: 'PPC-PDP-208', component: 'MCB-20A-1P', qtyPer: 24 },
  { fg: 'PPC-PDP-208', component: 'THHN-1/0-CU', qtyPer: 3 },
  { fg: 'KIT-VFD-50', component: 'ENC-N4X-2420', qtyPer: 1 },
  { fg: 'KIT-VFD-50', component: 'VFD-50HP-480', qtyPer: 1 },
  { fg: 'KIT-VFD-50', component: 'THHN-1/0-CU', qtyPer: 0.5 },
];

export const BOM_REVISION_BASE = 'Rev B';
export const BOM_REVISION_SWAPPED = 'Rev C';

export interface ComponentAlternate {
  sku: string;
  name: string;
  supplierId: SupplierId;
  unitPrice: number;
  leadTimeWeeks: number;
  /** `qualified` alternates can be swapped in today; `onboarding` ones cannot yet. */
  status: 'qualified' | 'onboarding';
  contractId?: ContractId;
  /** Short qualification note for the tree. */
  note: string;
}

export interface QualityHold {
  alertId: string;
  lot: string;
  scar: string;
  /** Quarantined units that cannot be used. */
  quarantinedQty: number;
  /** Open PO receipts from the held supplier, per bucket, excluded from netting. */
  heldReceipts: number[];
  heldPo: string;
  /** Clause the hold cites, as `${contractId}:${section}`. */
  clauseId: string;
}

export interface BomComponent {
  sku: ComponentSku;
  name: string;
  /** Unit of measure for quantities and prices. */
  uom: string;
  supplierId: SupplierId;
  /** Where the supplier delivers. */
  shipTo: SiteId;
  unitPrice: number;
  /** Clause id (`${contractId}:${section}`) that fixes the price, when one exists. */
  priceClauseId?: string;
  contractId: ContractId;
  leadTimeWeeks: number;
  /** Minimum order quantity. */
  moq: number;
  /** Orders round up to a multiple of this pack size. */
  multiple: number;
  onHand: number;
  safetyStock: number;
  /** Open PO receipts per bucket. */
  receipts: number[];
  /** Open PO numbers behind `receipts`. */
  receiptPos: string[];
  materialId?: MaterialId;
  alternates: ComponentAlternate[];
  hold?: QualityHold;
  /** Open risk alert on the component's sourcing, shown as a link. */
  riskAlertId?: string;
}

export const COMPONENTS: BomComponent[] = [
  {
    sku: 'ENC-N12-7224',
    name: 'Floor-standing enclosure, 72 x 24 in',
    uom: 'each',
    supplierId: 'sup-polaris',
    shipTo: 'PNL',
    unitPrice: 1420,
    contractId: 'SA-POLR-2022-008',
    leadTimeWeeks: 6,
    moq: 10,
    multiple: 5,
    onHand: 140,
    safetyStock: 20,
    receipts: [0, 0, 0, 0, 0, 0],
    receiptPos: [],
    alternates: [],
  },
  {
    sku: 'ENC-N4X-2420',
    name: 'Corrosion-resistant enclosure, 24 x 20 in',
    uom: 'each',
    supplierId: 'sup-polaris',
    shipTo: 'PNL',
    unitPrice: 690,
    contractId: 'SA-POLR-2022-008',
    leadTimeWeeks: 6,
    moq: 10,
    multiple: 5,
    onHand: 140,
    safetyStock: 20,
    receipts: [0, 0, 0, 0, 0, 0],
    receiptPos: [],
    alternates: [],
    riskAlertId: 'RSK-300',
  },
  {
    sku: 'MCCB-800A',
    name: 'Circuit breaker, 800 A',
    uom: 'each',
    supplierId: 'sup-vitrex',
    shipTo: 'PNL',
    unitPrice: 3850,
    priceClauseId: 'SA-VITX-2023-004:2.1',
    contractId: 'SA-VITX-2023-004',
    leadTimeWeeks: 10,
    moq: 10,
    multiple: 1,
    onHand: 64,
    safetyStock: 6,
    receipts: [0, 20, 0, 0, 0, 0],
    receiptPos: ['PO-4500183'],
    materialId: 'MAT-MCCB',
    alternates: [
      {
        sku: 'MCCB-800A-H',
        name: 'Hollis 800 A circuit breaker',
        supplierId: 'sup-hollis',
        unitPrice: 4020,
        leadTimeWeeks: 5,
        status: 'qualified',
        contractId: 'SA-HOLL-2024-021',
        note: 'Qualified alternate, same size and ratings',
      },
    ],
    hold: {
      alertId: 'RSK-301',
      lot: 'VX-24117',
      scar: 'SCAR-0192',
      quarantinedQty: 40,
      heldReceipts: [0, 60, 0, 0, 0, 0],
      heldPo: 'PO-4500184',
      clauseId: 'QA-VITX-2023-004:7.2',
    },
    riskAlertId: 'RSK-301',
  },
  {
    sku: 'VFD-50HP-480',
    name: 'Motor drive, 50 HP, 480 V',
    uom: 'each',
    supplierId: 'sup-lumen',
    shipTo: 'PNL',
    unitPrice: 5500,
    priceClauseId: 'SA-LUMN-2025-002:4.1',
    contractId: 'SA-LUMN-2025-002',
    leadTimeWeeks: 12,
    moq: 20,
    multiple: 10,
    onHand: 180,
    safetyStock: 40,
    receipts: [60, 0, 0, 0, 0, 0],
    receiptPos: ['PO-4500182'],
    materialId: 'MAT-VFD',
    alternates: [
      {
        sku: 'VFD-50HP-480',
        name: 'Kestrel 50 HP motor drive',
        supplierId: 'sup-kestrel',
        unitPrice: 6050,
        leadTimeWeeks: 8,
        status: 'onboarding',
        note: 'Second source in onboarding (ONB-0087)',
      },
    ],
    riskAlertId: 'RSK-305',
  },
  {
    sku: 'MCB-20A-1P',
    name: 'Small circuit breaker, 20 A, 1 pole',
    uom: 'each',
    supplierId: 'sup-hollis',
    shipTo: 'PNL',
    unitPrice: 13.6,
    contractId: 'SA-HOLL-2024-021',
    leadTimeWeeks: 5,
    moq: 120,
    multiple: 12,
    onHand: 1400,
    safetyStock: 200,
    receipts: [0, 0, 0, 0, 0, 0],
    receiptPos: [],
    alternates: [],
  },
  {
    sku: 'THHN-1/0-CU',
    name: 'Copper building wire, 1/0 gauge, 500 ft reel',
    uom: 'C',
    supplierId: 'sup-corvane',
    shipTo: 'DAL',
    unitPrice: 424.36,
    priceClauseId: 'SA-CORV-2024-017:3.3',
    contractId: 'SA-CORV-2024-017',
    leadTimeWeeks: 8,
    moq: 100,
    multiple: 5,
    onHand: 450,
    safetyStock: 60,
    receipts: [0, 250, 0, 0, 0, 0],
    receiptPos: ['PO-4500181'],
    materialId: 'MAT-THHN',
    alternates: [
      {
        sku: 'THHN-1/0-CU',
        name: 'Hollis 1/0 copper building wire',
        supplierId: 'sup-hollis',
        unitPrice: 437,
        leadTimeWeeks: 5,
        status: 'qualified',
        contractId: 'SA-HOLL-2024-021',
        note: 'Qualified second supplier, 33% share today',
      },
    ],
  },
];

export function getFinishedGood(sku: FgSku): FinishedGood {
  const found = FINISHED_GOODS.find((fg) => fg.sku === sku);
  if (!found) throw new Error(`Unknown finished good ${sku}`);
  return found;
}

export function getComponent(sku: ComponentSku): BomComponent {
  const found = COMPONENTS.find((component) => component.sku === sku);
  if (!found) throw new Error(`Unknown component ${sku}`);
  return found;
}

export function bomLinesForFg(fg: FgSku): BomLine[] {
  return BOM_LINES.filter((line) => line.fg === fg);
}

export function whereUsed(component: ComponentSku): BomLine[] {
  return BOM_LINES.filter((line) => line.component === component);
}
